import React, { useRef, useEffect, useState } from 'react';
import { Shield, ChevronRight, Star, ArrowUp, Bell } from 'lucide-react';
import { queryClient } from '../lib/queryClient';
import axios from 'axios';

const API = '/api';

/**
 * LiveBadges — diretta.it-style flash badges next to the team name.
 * iter109 (2026-02).
 *
 * Reads the server-provided `live_badges` timestamps and renders each
 * badge until N real seconds after the timestamp:
 *   GOAL       → 60s   (green)
 *   SBAGLIATO  → 60s   (dark gray)
 *   ANNULLATO  → 90s   (red-ish)
 *   VAR        → 120s  (blue)
 *
 * A single 1Hz setInterval re-renders the component so badges fade out
 * without waiting for the parent's data refetch (which lands every 30s).
 */
const BADGE_TTL_SECS = {
  goal: 60,
  missed: 60,
  cancelled: 90,
  var: 120,
};

/**
 * useLiveElapsed — iter238 (2026-02).
 *
 * Il minuto `elapsed` che ci arriva dal backend è statico: viene
 * aggiornato ogni ~15s dal refetch di React Query, e a monte API-Football
 * ha un lag intrinseco di 60-90s. Sommando i due, sullo schermo si
 * accumula un ritardo apparente di 3-6 minuti rispetto al vero live.
 *
 * Questo hook usa il valore server come "anchor" e incrementa lato
 * client di 1 minuto ogni 60 secondi reali. Al prossimo refetch, se il
 * server ritorna un `elapsed` NUOVO (partita ripartita, HT finito), lo
 * usa come nuovo anchor. Se il server ritorna un valore MOLTO diverso
 * (>3 min di scarto), si fida del server (probabile HT/pausa/interruzione).
 *
 * Zero chiamate API extra, zero costo. Effetto: il minuto scorre in
 * tempo reale come su Flashscore, non a scatti di 15s.
 *
 * Gestione status:
 *   • "1H" / "2H" / "ET" → contatore attivo (incrementa)
 *   • "HT" / "BT" / "P"  → contatore fermo (il minuto server è quello giusto)
 *   • Assente / non-live → ritorna il valore server as-is
 */
const useLiveElapsed = (serverElapsed, status, isLive) => {
  const [displayed, setDisplayed] = useState(serverElapsed);
  const anchorRef = useRef({ elapsed: serverElapsed, ts: Date.now(), status });

  useEffect(() => {
    if (serverElapsed == null || !isLive) {
      setDisplayed(serverElapsed);
      return;
    }
    // Se il server ha cambiato valore (o scarto >3 min), riallinea l'anchor.
    const currentClient = displayed || serverElapsed;
    const drift = Math.abs(serverElapsed - currentClient);
    const shouldResync = (
      serverElapsed !== anchorRef.current.elapsed ||
      status !== anchorRef.current.status ||
      drift >= 3
    );
    if (shouldResync) {
      anchorRef.current = { elapsed: serverElapsed, ts: Date.now(), status };
      setDisplayed(serverElapsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverElapsed, status, isLive]);

  useEffect(() => {
    // Contatore attivo solo in tempi di gioco effettivi (1H, 2H, ET).
    const runningStatuses = ['1H', '2H', 'ET'];
    if (!isLive || !runningStatuses.includes(status) || serverElapsed == null) {
      return;
    }
    const tick = () => {
      const anchor = anchorRef.current;
      const minutesSinceAnchor = Math.floor((Date.now() - anchor.ts) / 60000);
      const projected = anchor.elapsed + minutesSinceAnchor;
      // Cap sensato: max +5 min oltre l'anchor server (evita corse a 90'+++)
      const capped = Math.min(projected, anchor.elapsed + 5);
      setDisplayed(prev => (prev === capped ? prev : capped));
    };
    // Aggiorna ogni 15s per catturare il salto di minuto entro pochi secondi.
    const id = setInterval(tick, 15000);
    tick(); // primo update immediato
    return () => clearInterval(id);
  }, [isLive, status, serverElapsed]);

  return displayed;
};

const LiveBadges = ({ badges, side, fixtureId }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!badges) return undefined;
    // Only tick while there's at least one non-null timestamp for this side —
    // avoids unnecessary re-renders on 90%+ of the list.
    const anyActive =
      badges[`${side}_goal_at`] ||
      badges[`${side}_var_at`] ||
      badges[`${side}_goal_cancelled_at`] ||
      badges[`${side}_missed_penalty_at`];
    if (!anyActive) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [
    badges?.[`${side}_goal_at`],
    badges?.[`${side}_var_at`],
    badges?.[`${side}_goal_cancelled_at`],
    badges?.[`${side}_missed_penalty_at`],
  ]);

  if (!badges) return null;

  const now = Date.now();
  const withinTtl = (isoTs, ttl) => {
    if (!isoTs) return false;
    const t = new Date(isoTs).getTime();
    if (Number.isNaN(t)) return false;
    return now - t < ttl * 1000;
  };

  const showGoal = withinTtl(badges[`${side}_goal_at`], BADGE_TTL_SECS.goal);
  const showMissed = withinTtl(badges[`${side}_missed_penalty_at`], BADGE_TTL_SECS.missed);
  const showCancelled = withinTtl(badges[`${side}_goal_cancelled_at`], BADGE_TTL_SECS.cancelled);
  const showVar = withinTtl(badges[`${side}_var_at`], BADGE_TTL_SECS.var);

  if (!showGoal && !showMissed && !showCancelled && !showVar) return null;

  // Consume tick so React includes it in the closure (silences lint).
  void tick;

  // Order (left-to-right): GOAL — most exciting; VAR — in-progress; ANNULLATO
  // — bad news for goal-scorer; SBAGLIATO — final state.
  return (
    <span className="flex items-center gap-1 flex-shrink-0" data-testid={`live-badges-${side}-${fixtureId}`}>
      {showGoal && (
        <span
          className="px-1.5 py-0.5 rounded-[3px] text-[9px] font-black tracking-wider bg-emerald-500 text-white animate-pulse uppercase leading-none"
          data-testid={`live-goal-${side}-${fixtureId}`}
        >
          GOAL
        </span>
      )}
      {showVar && (
        <span
          className="px-1.5 py-0.5 rounded-[3px] text-[9px] font-black tracking-wider bg-blue-600 text-white uppercase leading-none"
          data-testid={`live-var-${side}-${fixtureId}`}
        >
          VAR
        </span>
      )}
      {showCancelled && (
        <span
          className="px-1.5 py-0.5 rounded-[3px] text-[9px] font-black tracking-wider bg-red-600 text-white uppercase leading-none"
          data-testid={`live-cancelled-${side}-${fixtureId}`}
        >
          ANNULLATO
        </span>
      )}
      {showMissed && (
        <span
          className="px-1.5 py-0.5 rounded-[3px] text-[9px] font-black tracking-wider bg-gray-600 text-white uppercase leading-none"
          data-testid={`live-missed-${side}-${fixtureId}`}
        >
          SBAGLIATO
        </span>
      )}
    </span>
  );
};

// Prefetch prediction when match row enters viewport
// BUGFIX 2026-04-24: skip prefetch entirely for non-VIP users — was causing
// a flood of 401 Unauthorized in backend logs (13+ per session). Non-VIP
// users cannot read predictions, so the prefetch just wastes bandwidth and
// pollutes logs / rate-limit counters.
const usePrefetchPrediction = (fixtureId, isFinished, isVip) => {
  const ref = useRef(null);
  const prefetched = useRef(false);

  useEffect(() => {
    if (!fixtureId || isFinished || prefetched.current || !isVip) return;
    const el = ref.current;
    if (!el) return;

    // Check if React Query has already fetched this recently.
    // Se la query e' in cache e fresca, skip prefetch.
    const cached = queryClient.getQueryData(['prediction', fixtureId, false]);
    const state = queryClient.getQueryState(['prediction', fixtureId, false]);
    if (cached && state && (Date.now() - state.dataUpdatedAt) < 5 * 60 * 1000) {
      prefetched.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !prefetched.current) {
          prefetched.current = true;
          // Defer the prefetch to the browser's idle time so a fast scroll
          // through hundreds of rows doesn't fire hundreds of network calls
          // + React Query bookkeeping ops on the main thread mid-frame.
          // Fallback to setTimeout on browsers without rIC (Safari <17).
          const schedule =
            typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
              ? (fn) => window.requestIdleCallback(fn, { timeout: 2000 })
              : (fn) => setTimeout(fn, 250);
          schedule(() => {
            // Prefetch with low priority — don't block other requests
            queryClient.prefetchQuery({
              queryKey: ['prediction', fixtureId, false],
              queryFn: async () => {
                const { data } = await axios.get(`${API}/predictions/${fixtureId}`, { timeout: 60000 });
                return data;
              },
              staleTime: 5 * 60 * 1000,
            });
          });
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // start 200px before visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fixtureId, isFinished, isVip]);

  return ref;
};

const MatchRow = ({ match, onNavigate, isFavorite, onToggleFavorite, isVip, isFollowing }) => {
  const liveStatuses = ["1H", "2H", "HT", "LIVE", "ET", "BREAK", "1° Tempo", "2° Tempo", "Intervallo", "1st Half", "2nd Half", "Half Time"];
  const finishedStatuses = ["FT", "AET", "PEN", "FT_PEN", "Full Time", "Terminata", "Finished", "After Extra Time", "Dopo supplementari", "Ai rigori"];
  const postponedStatuses = ["PST", "Rinviata", "Postponed", "TBD", "Da definire", "To Be Defined"];
  const cancelledStatuses = ["CANC", "Cancellata", "Cancelled", "ABD", "Abandoned", "Abbandonata", "AWD", "WO"];
  const suspendedStatuses = ["SUSP", "Sospesa", "Suspended", "INT", "Interrotta", "Interrupted"];
  const isLive = match.is_live || liveStatuses.includes(match.status);
  const isFinished = match.is_finished || finishedStatuses.includes(match.status);
  const isPostponed = postponedStatuses.includes(match.status);
  const isCancelled = cancelledStatuses.includes(match.status);
  const isSuspended = suspendedStatuses.includes(match.status);
  // iter238 (2026-02) — minuto live proiettato lato client (compensa lag
  // di API-Football + latenza refetch). Vedi hook `useLiveElapsed` sopra.
  const displayedElapsed = useLiveElapsed(match.elapsed, match.status_short || match.status, isLive);
  const hasScore = match.score_home !== null;
  const displayTime = match.match_time || match.time;

  // First-leg parziale (e.g. Serie B/C play-off final ritorno, UCL knockout)
  // We map the first-leg goals back to the CURRENT match's home/away order so
  // the user always sees their team's goals on the same line as their team.
  let firstLegHomeGoals = null;
  let firstLegAwayGoals = null;
  const fl = match.first_leg;
  if (fl && fl.home_team && fl.away_team
      && fl.score_home != null && fl.score_away != null) {
    const flHome = fl.home_team.toLowerCase();
    const flAway = fl.away_team.toLowerCase();
    const curHome = (match.home_team || '').toLowerCase();
    const curAway = (match.away_team || '').toLowerCase();
    if (flHome === curAway && flAway === curHome) {
      // Typical "ritorno": first leg was hosted by the current AWAY team
      firstLegHomeGoals = fl.score_away;
      firstLegAwayGoals = fl.score_home;
    } else if (flHome === curHome && flAway === curAway) {
      firstLegHomeGoals = fl.score_home;
      firstLegAwayGoals = fl.score_away;
    }
  }
  const showFirstLeg = firstLegHomeGoals != null;

  // iter99: penalty shootout & knockout winner. When a game ended at
  // penalties, API-Football sets `status_short = "PEN"` and populates
  // `penalty_home/away` in the fixture. The scoreboard mirrors diretta.it:
  //   "1 (4)   1 (3)   ↑ Sassuolo   • Dopo rigori"
  // For matches decided in extra time (status "AET") we show the arrow
  // on the winner but no parenthesized score.
  //
  // iter100 (bugfix): a knockout tie decided at 90' — e.g. "Udinese 2-1
  // Venezia" in Coppa Italia Round of 32 — must ALSO show the winner
  // arrow, matching diretta.it's behaviour. We detect the knockout stage
  // from `league_round` because API-Football exposes it as strings like
  // "Round of 32", "Round of 16", "Quarter-finals", "Semi-finals",
  // "Final", "Preliminary Round", "1st Round", etc. Regular league rounds
  // ("Regular Season - 10", "Girone A - 3") never match, so we don't
  // accidentally paint arrows on Serie A / league matches.
  const wentToPenalties = match.status_short === 'PEN'
    && match.penalty_home != null && match.penalty_away != null;
  const wentToExtraTime = match.status_short === 'AET';
  const roundStr = (match.league_round || '').toLowerCase();
  const isKnockoutStage = /round of|quarter|semi|final|preliminary|play[- ]?off|(^|\s)(1st|2nd|3rd|4th|5th|6th|7th|first|second|third|fourth|fifth) round/i
    .test(roundStr);
  const winnerSide = match.winner_side; // "home" | "away" | null
  const showWinnerArrow = isFinished && winnerSide
    && (wentToPenalties || wentToExtraTime || isKnockoutStage);
  const knockoutLabel = wentToPenalties ? 'Dopo rigori' : (wentToExtraTime ? 'Dopo supplementari' : null);

  const handleStarClick = (e) => {
    e.stopPropagation();
    onToggleFavorite?.(match.fixture_id);
  };

  const handleClick = () => {
    onNavigate?.(match.fixture_id);
  };

  const prefetchRef = usePrefetchPrediction(match.fixture_id, isFinished, isVip);

  return (
    <div
      ref={prefetchRef}
      onClick={handleClick}
      className="match-row-container flex items-center gap-2 px-2 py-2.5 cursor-pointer border-b border-white/5 active:bg-white/[0.03]"
      data-testid={`match-row-${match.fixture_id}`}
    >
      <button
        onClick={handleStarClick}
        className="relative flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full active:scale-90 transition-transform"
        data-testid={`favorite-star-${match.fixture_id}`}
      >
        <Star
          className={`w-4 h-4 ${isFavorite ? 'text-[#F5A623] fill-[#F5A623] drop-shadow-[0_0_4px_rgba(245,166,35,0.5)]' : 'text-gray-600'}`}
        />
        {isFollowing && (
          <Bell className="absolute -top-0.5 -right-0.5 w-3 h-3 text-[#F5A623] fill-[#F5A623]" data-testid={`match-follow-bell-${match.fixture_id}`} />
        )}
      </button>

      <div className="w-12 flex-shrink-0 text-center">
        {isLive ? (
          <div className="flex flex-col items-center gap-0.5">
            <span className="px-1.5 py-0.5 bg-[#FF0055] text-white text-[9px] font-black rounded animate-pulse">LIVE</span>
            <span className="text-[10px] font-mono font-bold text-[#FF0055]">{displayedElapsed != null ? `${displayedElapsed}'` : (match.elapsed ? `${match.elapsed}'` : match.status)}</span>
          </div>
        ) : isFinished ? (
          <span className="text-[10px] font-bold text-gray-500">FIN</span>
        ) : isPostponed ? (
          <span
            className="text-[10px] font-bold text-amber-400 uppercase tracking-wide"
            title="Partita rinviata"
            data-testid={`status-postponed-${match.fixture_id}`}
          >
            Post.
          </span>
        ) : isCancelled ? (
          <span
            className="text-[10px] font-bold text-red-400 uppercase tracking-wide"
            title="Partita annullata"
            data-testid={`status-cancelled-${match.fixture_id}`}
          >
            Ann.
          </span>
        ) : isSuspended ? (
          <span
            className="text-[10px] font-bold text-orange-400 uppercase tracking-wide"
            title="Partita sospesa"
            data-testid={`status-suspended-${match.fixture_id}`}
          >
            Sosp.
          </span>
        ) : (
          <span className="text-xs font-mono font-bold text-gray-300">{displayTime}</span>
        )}
      </div>
      
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex-shrink-0 rounded bg-#333333/50 p-0.5">
            {match.home_logo ? (
              <img
                src={match.home_logo}
                alt=""
                width="20"
                height="20"
                decoding="async"
                className="w-full h-full object-contain"
              />
            ) : (
              <Shield className="w-full h-full text-gray-500" />
            )}
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-sm font-semibold text-white truncate">{match.home_team}</span>
            <LiveBadges badges={match.live_badges} side="home" fixtureId={match.fixture_id} />
            {showWinnerArrow && winnerSide === 'home' && (
              <ArrowUp
                className="w-3.5 h-3.5 text-[#F5A623] flex-shrink-0"
                strokeWidth={3}
                data-testid={`winner-arrow-home-${match.fixture_id}`}
              />
            )}
            {showFirstLeg && (
              <span
                className="text-xs font-bold tabular-nums text-gray-500/80 flex-shrink-0"
                title={`Andata: ${fl.home_team} ${fl.score_home}-${fl.score_away} ${fl.away_team}`}
                data-testid={`first-leg-home-${match.fixture_id}`}
              >
                {firstLegHomeGoals}
              </span>
            )}
          </div>
          {match.home_red_cards > 0 && (
            <span className="w-[14px] h-[18px] bg-red-600 rounded-[2px] flex-shrink-0 inline-block" data-testid={`red-card-home-${match.fixture_id}`} />
          )}
          {hasScore && (
            <span className={`text-lg font-black font-['Barlow_Condensed'] tabular-nums text-right ${isLive ? 'text-yellow-400' : 'text-white'}`}>
              {match.score_home}
              {wentToPenalties && (
                <span
                  className="text-xs font-bold text-gray-400 ml-0.5 align-super"
                  data-testid={`penalty-home-${match.fixture_id}`}
                >
                  ({match.penalty_home})
                </span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex-shrink-0 rounded bg-#333333/50 p-0.5">
            {match.away_logo ? (
              <img
                src={match.away_logo}
                alt=""
                width="20"
                height="20"
                decoding="async"
                className="w-full h-full object-contain"
              />
            ) : (
              <Shield className="w-full h-full text-gray-500" />
            )}
          </div>
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="text-sm font-semibold text-white truncate">{match.away_team}</span>
            <LiveBadges badges={match.live_badges} side="away" fixtureId={match.fixture_id} />
            {showWinnerArrow && winnerSide === 'away' && (
              <ArrowUp
                className="w-3.5 h-3.5 text-[#F5A623] flex-shrink-0"
                strokeWidth={3}
                data-testid={`winner-arrow-away-${match.fixture_id}`}
              />
            )}
            {showFirstLeg && (
              <span
                className="text-xs font-bold tabular-nums text-gray-500/80 flex-shrink-0"
                title={`Andata: ${fl.home_team} ${fl.score_home}-${fl.score_away} ${fl.away_team}`}
                data-testid={`first-leg-away-${match.fixture_id}`}
              >
                {firstLegAwayGoals}
              </span>
            )}
          </div>
          {match.away_red_cards > 0 && (
            <span className="w-[14px] h-[18px] bg-red-600 rounded-[2px] flex-shrink-0 inline-block" data-testid={`red-card-away-${match.fixture_id}`} />
          )}
          {hasScore && (
            <span className={`text-lg font-black font-['Barlow_Condensed'] tabular-nums text-right ${isLive ? 'text-yellow-400' : 'text-white'}`}>
              {match.score_away}
              {wentToPenalties && (
                <span
                  className="text-xs font-bold text-gray-400 ml-0.5 align-super"
                  data-testid={`penalty-away-${match.fixture_id}`}
                >
                  ({match.penalty_away})
                </span>
              )}
            </span>
          )}
        </div>
        {knockoutLabel && (
          <div
            className="text-[10px] font-semibold text-[#F5A623]/80 uppercase tracking-wider mt-0.5 pl-7"
            data-testid={`knockout-label-${match.fixture_id}`}
          >
            {knockoutLabel}
          </div>
        )}
      </div>
      
      <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
    </div>
  );
};

export default React.memo(MatchRow);
